import { useCallback } from 'react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { API_ROUTES } from '@/api/apiRoutes';
import { safeAxios } from '@/services/safeAxios';
import { log } from '@/logger/logger';
import {
  FF_BLOCK_FREE_USER_CUSTOM_EFFECT,
  FF_CAMERA_NODE_POC,
  FF_CHIRP_3_TRANSCRIPTION,
  FF_CUSTOM_GLSL_PRICING,
  FF_KLING_V4_FLASH,
} from '@/consts/featureFlags';
import { applyCameraPocMenuEntry, applyCustomGlslPricing } from '@/state/nodes/menu-overlays';
import { useNodeFiltersStore } from '@/state/nodes/nodes.state';
import { parseMenuData } from '@/state/nodes/nodes-parser';
import { usePrefetchSubflowTemplates } from '@/queries/useSubflowTemplates';
import { useSubflowGroupsEnabled } from '@/components/SubflowTemplates/useSubflowGroupsEnabled';
import { useSavedNodesEnabled } from '@/components/SubflowTemplates/useSavedNodesEnabled';
import { useSubscriptionPermissions } from '@/hooks/useSubscriptionPermissions/useSubscriptionPermissions';
import { SubscriptionType } from '@/types/shared';

const logger = log.getLogger('useFetchMenuItems');

export const useFetchMenuItems = () => {
  const isCameraNodePocEnabled = useFeatureFlagEnabled(FF_CAMERA_NODE_POC);
  const isCustomGlslPricingEnabled = useFeatureFlagEnabled(FF_CUSTOM_GLSL_PRICING);
  const isBlockFreeUserCustomEffectEnabled = useFeatureFlagEnabled(FF_BLOCK_FREE_USER_CUSTOM_EFFECT);
  const { isSubscriptionTypeIn } = useSubscriptionPermissions();
  const showCustomGlslPaidPlanNote =
    isSubscriptionTypeIn([SubscriptionType.Free]) && !!isBlockFreeUserCustomEffectEnabled;
  const isChirpTranscriptionEnabled = useFeatureFlagEnabled(FF_CHIRP_3_TRANSCRIPTION);
  const isKlingV4FlashEnabled = useFeatureFlagEnabled(FF_KLING_V4_FLASH);
  const isSavedNodesEnabled = useSavedNodesEnabled();
  const isSubflowGroupsEnabled = useSubflowGroupsEnabled();
  const { setMenuData, setIsMenuDataLoaded } = useNodeFiltersStore();
  const prefetchSubflowTemplates = usePrefetchSubflowTemplates();

  /** Resolves false when the menu couldn't load, which leaves the previous menu in place. */
  const fetchMenuItems = useCallback(async (): Promise<boolean> => {
    try {
      // Sent unconditionally: harmless on the new path, required on the old path so users whose
      // models were already moved to the new table still see them if the new flow is turned off.
      const res = await safeAxios.get(API_ROUTES.nodeDefinitions.menu(), {
        params: { includeMigrated: true },
      });
      // The rows carry categories, so the server files them into sections and applies each one's
      // rollout flag; only Gen Effect's price still has to come from the client.
      res.data.menu.tools ??= [];
      applyCustomGlslPricing(res.data, !!isCustomGlslPricingEnabled, showCustomGlslPaidPlanNote);
      applyCameraPocMenuEntry(res.data, !!isCameraNodePocEnabled);

      if (isSavedNodesEnabled) {
        prefetchSubflowTemplates();
      }

      if (!isChirpTranscriptionEnabled && Array.isArray(res.data.menu.models)) {
        const stripHidden = (nodes: any[]): any[] =>
          nodes
            .filter((n) => n.modelName !== 'chirp-3-transcription')
            .map((n) => (Array.isArray(n.children) ? { ...n, children: stripHidden(n.children) } : n));
        res.data.menu.models = stripHidden(res.data.menu.models);
      }

      if (!isKlingV4FlashEnabled) {
        type MenuEntry = { modelName?: string; children?: MenuEntry[] };
        const stripKling = (nodes: MenuEntry[]): MenuEntry[] =>
          nodes
            .filter((node) => !node.modelName?.startsWith('kling/v4/'))
            .map((node) => (Array.isArray(node.children) ? { ...node, children: stripKling(node.children) } : node));
        for (const section of Object.keys(res.data.menu)) {
          if (Array.isArray(res.data.menu[section])) {
            res.data.menu[section] = stripKling(res.data.menu[section]);
          }
        }
        if (Array.isArray(res.data.recent)) {
          res.data.recent = stripKling(res.data.recent);
        }
      }

      const parsedMenuData = parseMenuData(res.data, {
        includeSavedTemplates: isSavedNodesEnabled,
        includeSubflows: isSubflowGroupsEnabled,
        // When the new saved-models panel is on, hide the user's old saves from the old Custom
        // Models section (and search and add-node menu) so they only show up in one place.
        excludeUserModels: !!isSavedNodesEnabled,
      });

      setMenuData(parsedMenuData);
      return true;
    } catch (e) {
      logger.error('Error fetching menu items', e);
      return false;
    } finally {
      setIsMenuDataLoaded(true);
    }
  }, [
    setMenuData,
    setIsMenuDataLoaded,
    isCameraNodePocEnabled,
    isCustomGlslPricingEnabled,
    showCustomGlslPaidPlanNote,
    isChirpTranscriptionEnabled,
    isKlingV4FlashEnabled,
    isSavedNodesEnabled,
    isSubflowGroupsEnabled,
    prefetchSubflowTemplates,
  ]);

  return { fetchMenuItems };
};
